import { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Minus, Plus, Loader2, Check, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { estimatePrice, isRecurring } from "@/data/site";
import { submitLead, createCheckout } from "@/lib/api";
import { usePricing } from "@/context/PricingProvider";
import { useKeyboardOpen } from "@/hooks/useKeyboardOpen";
import { AddonsPicker, emptyAddons, addonsTotal, addonsSummary } from "@/components/AddonsPicker";
import { RecurringPromise, OfferBanner } from "@/components/RecurringPromise";

const PROPERTY_TYPES = ["House", "Townhouse", "Condo", "Apartment"];
const CLEAN_TYPES = ["Standard Cleaning", "Deep Cleaning", "Move-In / Move-Out"];
const FREQUENCIES = ["One-time", "Weekly", "Biweekly", "Monthly"];
const SQFT_BUCKETS = [
  { label: "Under 800 sq ft", value: 700 },
  { label: "800–1,500 sq ft", value: 1200 },
  { label: "1,500–2,500 sq ft", value: 2000 },
  { label: "2,500–3,500 sq ft", value: 3000 },
  { label: "3,500+ sq ft", value: 4000 },
];
const DEPOSIT_THRESHOLD = 200;
const DEPOSIT_AMOUNT = 50;

const Stepper = ({ label, value, set, min = 1, max = 8, testid }) => (
  <div className="flex items-center justify-between bg-white rounded-2xl border border-border px-3 py-3">
    <span className="font-medium text-navy text-sm">{label}</span>
    <div className="flex items-center gap-2">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => set(Math.max(min, value - 1))}
        className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-navy hover:bg-muted transition-colors shrink-0"><Minus className="w-4 h-4" /></button>
      <span className="w-5 text-center font-semibold text-navy" data-testid={`${testid}-value`}>{value}</span>
      <button type="button" data-testid={`${testid}-plus`} onClick={() => set(Math.min(max, value + 1))}
        className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-navy hover:bg-muted transition-colors shrink-0"><Plus className="w-4 h-4" /></button>
    </div>
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 border ${active ? "bg-navy text-white border-navy" : "bg-white text-navy border-border hover:border-leaf"}`}>
    {children}
  </button>
);

export const QuoteEstimator = ({ mode = "quote", onClose, onBook }) => {
  const isBooking = mode === "booking";
  const pricing = usePricing();
  const keyboardOpen = useKeyboardOpen();
  const [propertyType, setPropertyType] = useState("House");
  const [bedrooms, setBedrooms] = useState(2);
  const [bathrooms, setBathrooms] = useState(2);
  const [sqft, setSqft] = useState(1150);
  const [cleaningType, setCleaningType] = useState("Standard Cleaning");
  const [frequency, setFrequency] = useState("One-time");
  const [addonQty, setAddonQty] = useState(emptyAddons());
  const [preferredDate, setPreferredDate] = useState("");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const detailsRef = useRef(null);

  const est = useMemo(
    () => estimatePrice({ bedrooms, bathrooms, sqft, cleaningType, propertyType, frequency, addonsTotal: addonsTotal(addonQty, pricing.addons) }, pricing),
    [bedrooms, bathrooms, sqft, cleaningType, propertyType, frequency, addonQty, pricing]
  );
  const depositNeeded = isBooking && est.low > DEPOSIT_THRESHOLD;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!contact.name.trim()) errs.name = true;
    if (!/\S+@\S+\.\S+/.test(contact.email)) errs.email = true;
    if (Object.keys(errs).length) {
      setErrors(errs);
      detailsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error("Please add your name and a valid email to continue.");
      return;
    }
    setErrors({});
    setLoading(true);
    const label = isBooking ? "Booking" : "Instant Quote";
    const selectedAddons = addonsSummary(addonQty, pricing.addons);
    const summary = `${label} — ${cleaningType}, ${propertyType}, ${bedrooms} bd / ${bathrooms} ba, ~${sqft} sq ft, ${frequency}${selectedAddons ? `. Add-ons: ${selectedAddons}` : ""}${preferredDate ? `, preferred ${preferredDate}` : ""}. Estimated $${est.low}–$${est.high}.`;
    try {
      const lead = await submitLead({
        type: isBooking ? "booking" : "quote",
        name: contact.name, email: contact.email, phone: contact.phone,
        service_category: "residential", service_type: cleaningType, property_type: propertyType,
        frequency, preferred_date: preferredDate, addons: selectedAddons,
        estimate_low: est.low, estimate_high: est.high, message: summary,
      });
      if (isBooking && lead.deposit_required) {
        toast.success("Booking saved — redirecting to secure deposit checkout…");
        const { checkout_url } = await createCheckout(lead.id, window.location.origin);
        window.location.href = checkout_url;
        return;
      }
      setDone(true);
      if (!isBooking) toast.success(`Quote sent to ${contact.email}!`);
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center px-6 py-10" data-testid="quote-success">
        <div className="w-16 h-16 rounded-full bg-leaf mx-auto flex items-center justify-center mb-5"><Check className="w-8 h-8 text-white" /></div>
        <h3 className="font-heading text-2xl font-semibold text-navy mb-2">
          {isBooking ? "Booking request received!" : "Your estimate is ready!"}
        </h3>
        <p className="text-muted-foreground mb-2">Estimated total for your {cleaningType.toLowerCase()}:</p>
        <p className="font-heading text-4xl font-bold text-navy mb-5">${est.low} – ${est.high}</p>
        <p className="text-sm text-muted-foreground mb-7 max-w-sm mx-auto">
          {isBooking
            ? "We've received your booking and will confirm your date and exact price shortly. Green Cleaning is included."
            : `We've emailed your quote to ${contact.email}. We'll follow up to confirm your exact price — or lock in a date now.`}
        </p>
        {isBooking ? (
          <button onClick={() => onClose?.()} data-testid="quote-close-btn"
            className="px-7 py-4 rounded-full bg-navy text-white font-medium inline-flex items-center gap-2 hover:scale-105 transition-transform">
            Done
          </button>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={() => onBook?.({ propertyType, bedrooms, bathrooms, sqft, cleaningType, frequency, addonQty, contact, date: preferredDate ? new Date(preferredDate) : null })} data-testid="quote-book-btn"
              className="group px-7 py-4 rounded-full bg-leaf text-white font-medium inline-flex items-center gap-2 hover:scale-105 transition-transform">
              Book This Cleaning <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => onClose?.()} data-testid="quote-close-btn"
              className="px-7 py-4 rounded-full border border-border text-navy font-medium hover:bg-muted transition-colors">
              Close
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col min-h-0 flex-1" data-testid="quote-estimator">
      {/* Scrollable options */}
      <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
        <div>
          <Label className="text-navy/70 text-[11px] uppercase tracking-widest">Property type</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {PROPERTY_TYPES.map((t) => <Chip key={t} active={propertyType === t} onClick={() => setPropertyType(t)} testid={`qe-property-${t}`}>{t}</Chip>)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Stepper label="Bedrooms" value={bedrooms} set={setBedrooms} min={0} max={8} testid="qe-bedrooms" />
          <Stepper label="Bathrooms" value={bathrooms} set={setBathrooms} min={1} max={6} testid="qe-bathrooms" />
        </div>

        <div>
          <Label className="text-navy/70 text-[11px] uppercase tracking-widest">Square footage</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {SQFT_BUCKETS.map((b) => <Chip key={b.value} active={sqft === b.value} onClick={() => setSqft(b.value)} testid={`qe-sqft-${b.value}`}>{b.label}</Chip>)}
          </div>
        </div>

        <div>
          <Label className="text-navy/70 text-[11px] uppercase tracking-widest">Cleaning type</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {CLEAN_TYPES.map((t) => <Chip key={t} active={cleaningType === t} onClick={() => setCleaningType(t)} testid={`qe-type-${t}`}>{t}</Chip>)}
          </div>
        </div>

        <div>
          <Label className="text-navy/70 text-[11px] uppercase tracking-widest">Frequency</Label>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {FREQUENCIES.map((t) => <Chip key={t} active={frequency === t} onClick={() => setFrequency(t)} testid={`qe-freq-${t}`}>{t}</Chip>)}
          </div>
        </div>

        {isRecurring(frequency) && (
          <div className="space-y-3">
            <OfferBanner offer={pricing.first_clean_offer} />
            <RecurringPromise variant="card" promise={pricing.recurring_promise} />
          </div>
        )}

        <div>
          <Label className="text-navy/70 text-[11px] uppercase tracking-widest">Add-ons (optional)</Label>
          <div className="mt-1.5"><AddonsPicker qty={addonQty} setQty={setAddonQty} addons={pricing.addons} /></div>
        </div>

        {isBooking && (
          <div>
            <Label htmlFor="qe-date" className="text-navy/70 text-[11px] uppercase tracking-widest">Preferred date</Label>
            <Input id="qe-date" type="date" data-testid="qe-date" className="h-11 bg-white mt-1.5"
              value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} />
          </div>
        )}

        <div ref={detailsRef}>
          <Label className="text-navy/70 text-[11px] uppercase tracking-widest">Your details</Label>
          <div className="grid sm:grid-cols-3 gap-2 mt-1.5">
            <Input data-testid="qe-name" className={`h-11 bg-white ${errors.name ? "border-red-400 ring-1 ring-red-300" : ""}`} placeholder="Name*" value={contact.name} onChange={(e) => { setContact({ ...contact, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: false }); }} />
            <Input data-testid="qe-email" type="email" className={`h-11 bg-white ${errors.email ? "border-red-400 ring-1 ring-red-300" : ""}`} placeholder="Email*" value={contact.email} onChange={(e) => { setContact({ ...contact, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: false }); }} />
            <Input data-testid="qe-phone" className="h-11 bg-white" placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          </div>
          {(errors.name || errors.email) && (
            <p className="text-xs text-red-500 mt-1.5" data-testid="qe-details-error">Please enter your name and a valid email to get your quote.</p>
          )}
        </div>

        {depositNeeded && (
          <div className="flex items-start gap-3 rounded-2xl bg-leaf/10 border border-leaf/30 p-3" data-testid="qe-deposit-note">
            <ShieldCheck className="w-5 h-5 text-leaf shrink-0 mt-0.5" />
            <p className="text-sm text-navy">A refundable <strong>${DEPOSIT_AMOUNT} deposit</strong> secures your booking (applied to your total).</p>
          </div>
        )}
      </div>

      {/* Pinned footer: live estimate + actions */}
      <div className={`shrink-0 border-t border-border bg-white/95 backdrop-blur-sm px-6 space-y-3 ${keyboardOpen ? "py-2" : "py-4"}`}>
        <div className={`rounded-2xl bg-navy text-white px-5 py-3.5 items-center justify-between ${keyboardOpen ? "hidden" : "flex"}`} data-testid="qe-estimate">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-leaf flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Estimated price</p>
            <p className="font-heading text-2xl font-bold mt-0.5" data-testid="qe-estimate-value">${est.low} – ${est.high}</p>
          </div>
          <p className="text-[11px] text-white/50 max-w-[150px] text-right leading-snug">Green Cleaning included. Final price confirmed after review.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2.5">
          {!isBooking && onBook && !keyboardOpen && (
            <button type="button" onClick={() => onBook()} data-testid="qe-booknow-btn"
              className="group flex-1 py-3.5 rounded-full bg-navy text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform">
              Book Now <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          )}
          <button type="submit" disabled={loading} data-testid="qe-submit"
            className="group flex-1 py-3.5 rounded-full bg-leaf text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{isBooking ? (depositNeeded ? "Book & Pay Deposit" : "Request Booking") : "Get My Quote"} <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      </div>
    </form>
  );
};
